import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Calendar,
  AlertTriangle,
  Check,
  Edit2,
  ExternalLink,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { API, getPlatformName } from "./constants";

const DateManagement = ({ lang = "ko" }) => {
  const [suspiciousPosts, setSuspiciousPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [postsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/suspicious-dates?limit=50`),
        axios.get(`${API}/admin/date-quality-stats`),
      ]);
      setSuspiciousPosts(postsRes.data.posts || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch date quality data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveDate = async (postId) => {
    if (!editDate) {
      toast.error("날짜를 입력해 주세요");
      return;
    }
    setSaving(true);
    try {
      const isoDate = new Date(editDate).toISOString();
      await axios.patch(`${API}/admin/posts/${postId}/published-at`, {
        published_at: isoDate,
      });
      toast.success("게시 날짜가 수정되었습니다");
      setEditingId(null);
      setEditDate("");
      fetchData();
    } catch (error) {
      toast.error("날짜 수정 실패: " + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Overview */}
      {stats && (
        <Card data-testid="date-quality-stats">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              게시 날짜 품질 현황
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="text-2xl font-bold text-emerald-600">{stats.api_dates}</div>
                <div className="text-xs text-muted-foreground mt-1">API (정상)</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <div className="text-2xl font-bold text-cyan-600">{stats.id_derived_dates || 0}</div>
                <div className="text-xs text-muted-foreground mt-1">ID 추출 (자동복구)</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="text-2xl font-bold text-amber-600">{stats.fallback_dates}</div>
                <div className="text-xs text-muted-foreground mt-1">Fallback (수정 필요)</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="text-2xl font-bold text-blue-600">{stats.manual_dates}</div>
                <div className="text-xs text-muted-foreground mt-1">수동 수정됨</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-500/10 border border-gray-500/20">
                <div className="text-2xl font-bold text-gray-600">{stats.unknown_dates}</div>
                <div className="text-xs text-muted-foreground mt-1">미분류</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suspicious Posts List */}
      <Card data-testid="suspicious-dates-list">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            날짜 수정이 필요한 게시물
          </CardTitle>
          <CardDescription>
            동기화 시 실제 게시 날짜를 가져오지 못해 동기화 시점이 저장된 게시물입니다.
            실제 게시 날짜를 입력해 주세요.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suspiciousPosts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="no-suspicious-posts">
              <Check className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
              수정이 필요한 게시물이 없습니다
            </div>
          ) : (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {suspiciousPosts.map((post) => (
                <div
                  key={post.id || post.post_id}
                  className="p-3 rounded-lg border border-border/50 hover:border-border transition-colors"
                  data-testid={`suspicious-post-${post.post_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs shrink-0">
                          {getPlatformName(post.platform, lang)}
                        </Badge>
                        <span className="text-sm font-medium truncate">
                          {post.title || post.post_id}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>
                          현재 날짜: <span className="text-amber-600 font-medium">
                            {post.published_at ? format(new Date(post.published_at), "yyyy-MM-dd HH:mm") : "없음"}
                          </span>
                        </span>
                        {post.first_synced_at && (
                          <span>
                            동기화 시점: {format(new Date(post.first_synced_at), "yyyy-MM-dd HH:mm")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {post.post_url && (
                        <a
                          href={post.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingId(post.id || post.post_id);
                          setEditDate("");
                        }}
                        data-testid={`edit-date-btn-${post.post_id}`}
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        수정
                      </Button>
                    </div>
                  </div>

                  {/* Edit form */}
                  {editingId === (post.id || post.post_id) && (
                    <div className="mt-3 flex items-center gap-2 p-2 rounded bg-muted/50">
                      <Input
                        type="datetime-local"
                        value={editDate}
                        onChange={(e) => setEditDate(e.target.value)}
                        className="flex-1 h-8 text-sm"
                        data-testid={`date-input-${post.post_id}`}
                      />
                      <Button
                        size="sm"
                        onClick={() => handleSaveDate(post.id || post.post_id)}
                        disabled={saving || !editDate}
                        data-testid={`save-date-btn-${post.post_id}`}
                      >
                        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                        저장
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(null);
                          setEditDate("");
                        }}
                      >
                        취소
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DateManagement;
